import {
    Alert, Box, Chip, Link, Paper, Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography,
} from '@mui/material';
import { SeoReport } from './seoTypes';
import { ValueDelta, Sparkline, fmtInt, fmtPos, pct, colors } from './seoFormat';
import {
    adMoves, keywordGaps, rewriteCandidates, topMovers, AdMove, KeywordGap, PageMover, RewriteCandidate,
} from './seoInsightsCompute';

interface Props {
    report: SeoReport;
    sel: number;
}

const SubHeading = ({ children, hint }: { children: React.ReactNode; hint?: string }) => (
    <Box sx={{ mt: 3, mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {children}
        </Typography>
        {hint && (
            <Typography variant="caption" color="text.secondary">
                {hint}
            </Typography>
        )}
    </Box>
);

const PageCell = ({ label, url }: { label: string; url: string | null }) =>
    url ? (
        <Link href={url} target="_blank" rel="noopener" underline="hover" sx={{ fontWeight: 500 }}>
            {label}
        </Link>
    ) : (
        <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
            {label}
        </Typography>
    );

const fmtCtr = (v: number): string => `${(v * 100).toFixed(2)}%`;
const fmtMoney = (v: number): string => `$${Math.round(v).toLocaleString('en-US')}`;

// ---- Top movers ----

const MoverTable = ({ rows, dir }: { rows: PageMover[]; dir: 'up' | 'down' }) => (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 680 }}>
            <TableHead>
                <TableRow>
                    <TableCell>{dir === 'up' ? '▲ Gainers' : '▼ Decliners'}</TableCell>
                    <TableCell>Group</TableCell>
                    <TableCell align="right">Impressions</TableCell>
                    <TableCell align="right">Clicks</TableCell>
                    <TableCell align="right">Avg pos</TableCell>
                    <TableCell>Trend</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((m) => (
                    <TableRow key={`${dir}-${m.label}`}>
                        <TableCell><PageCell label={m.label} url={m.url} /></TableCell>
                        <TableCell><Typography variant="caption" color="text.secondary">{m.group}</Typography></TableCell>
                        <TableCell align="right"><ValueDelta text={fmtInt(m.impr)} p={pct(m.impr, m.imprPrev)} /></TableCell>
                        <TableCell align="right"><ValueDelta text={fmtInt(m.clicks)} p={pct(m.clicks, m.clicksPrev)} /></TableCell>
                        <TableCell align="right"><ValueDelta text={fmtPos(m.pos)} p={pct(m.pos, m.posPrev)} lowerBetter /></TableCell>
                        <TableCell><Sparkline data={m.series} /></TableCell>
                    </TableRow>
                ))}
                {rows.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={6}>
                            <Typography variant="caption" color="text.secondary">No significant {dir === 'up' ? 'gainers' : 'decliners'} this month.</Typography>
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    </TableContainer>
);

// ---- Rewrite candidates ----

const RewriteTable = ({ rows }: { rows: RewriteCandidate[] }) => (
    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
                <TableRow>
                    <TableCell>Page</TableCell>
                    <TableCell align="right">Refreshed</TableCell>
                    <TableCell align="right">Impressions</TableCell>
                    <TableCell align="right">Avg pos</TableCell>
                    <TableCell align="right">CTR</TableCell>
                    <TableCell>Why</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((r) => (
                    <TableRow key={r.label}>
                        <TableCell><PageCell label={r.label} url={r.url} /></TableCell>
                        <TableCell align="right">
                            <Typography variant="caption" color={r.ageMonths != null && r.ageMonths >= 12 ? 'error' : 'text.secondary'}>
                                {r.ageMonths == null ? '—' : `${r.ageMonths}mo ago`}
                            </Typography>
                        </TableCell>
                        <TableCell align="right">{fmtInt(r.impr)}</TableCell>
                        <TableCell align="right">{fmtPos(r.pos)}</TableCell>
                        <TableCell align="right">{fmtCtr(r.ctr)}</TableCell>
                        <TableCell>
                            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                {r.reasons.map((reason) => (
                                    <Chip key={reason} label={reason} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                                ))}
                            </Stack>
                        </TableCell>
                    </TableRow>
                ))}
                {rows.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={6}><Typography variant="caption" color="text.secondary">No clear rewrite candidates — pages look fresh and well-ranked.</Typography></TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    </TableContainer>
);

// ---- Keyword optimization gaps ----

const KeywordTable = ({ rows }: { rows: KeywordGap[] }) => (
    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
                <TableRow>
                    <TableCell>Query</TableCell>
                    <TableCell>Theme</TableCell>
                    <TableCell>Top landing page</TableCell>
                    <TableCell align="right">Impressions</TableCell>
                    <TableCell align="right">Avg pos</TableCell>
                    <TableCell align="right">CTR</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((k) => (
                    <TableRow key={k.q}>
                        <TableCell sx={{ fontWeight: 500 }}>{k.q}</TableCell>
                        <TableCell><Typography variant="caption" color="text.secondary">{k.theme}</Typography></TableCell>
                        <TableCell><Typography variant="caption" color="text.secondary">{k.page}</Typography></TableCell>
                        <TableCell align="right"><ValueDelta text={fmtInt(k.impr)} p={pct(k.impr, k.imprPrev)} /></TableCell>
                        <TableCell align="right">{fmtPos(k.pos)}</TableCell>
                        <TableCell align="right">{fmtCtr(k.ctr)}</TableCell>
                    </TableRow>
                ))}
                {rows.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={6}><Typography variant="caption" color="text.secondary">No queries sitting in the page 1-bottom / page 2 opportunity band this month.</Typography></TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    </TableContainer>
);

// ---- Paid-search moves ----

const AdTable = ({ rows, mode }: { rows: AdMove[]; mode: 'candidate' | 'cannibal' }) => (
    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 620 }}>
            <TableHead>
                <TableRow>
                    <TableCell>Query</TableCell>
                    <TableCell>Theme</TableCell>
                    <TableCell align="right">Impressions</TableCell>
                    <TableCell align="right">Organic pos</TableCell>
                    <TableCell align="right">{mode === 'candidate' ? 'Paid now' : 'Paid spend (90d)'}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((a) => (
                    <TableRow key={a.q}>
                        <TableCell sx={{ fontWeight: 500 }}>{a.q}</TableCell>
                        <TableCell><Typography variant="caption" color="text.secondary">{a.theme}</Typography></TableCell>
                        <TableCell align="right">{fmtInt(a.impr)}</TableCell>
                        <TableCell align="right">{fmtPos(a.pos)}</TableCell>
                        <TableCell align="right">
                            {mode === 'candidate'
                                ? (a.paid ? <Typography component="span" variant="caption" sx={{ color: colors.MUTED }}>{fmtMoney(a.paidCost)}</Typography> : <Typography component="span" variant="caption" sx={{ color: colors.UP, fontWeight: 600 }}>not bid</Typography>)
                                : fmtMoney(a.paidCost)}
                        </TableCell>
                    </TableRow>
                ))}
                {rows.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={5}>
                            <Typography variant="caption" color="text.secondary">
                                {mode === 'candidate' ? 'No strong ad candidates this month.' : 'No cannibalization detected.'}
                            </Typography>
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    </TableContainer>
);

export const SeoInsights = ({ report, sel }: Props) => {
    const movers = topMovers(report, sel);
    const rewrites = rewriteCandidates(report, sel);
    const gaps = keywordGaps(report, sel);
    const ads = adMoves(report, sel);
    const adsOn = report.ads?.configured;

    return (
        <Box>
            <SubHeading hint="Pages with the biggest month-over-month change in clicks (then impressions). Lower avg position is better.">
                Top movers
            </SubHeading>
            <MoverTable rows={movers.up} dir="up" />
            <MoverTable rows={movers.down} dir="down" />

            <SubHeading hint="Pages worth rewriting — ranked by wasted impressions × how stale the content is (sitemap last-modified). Refresh for ranking + conversion.">
                Rewrite candidates
            </SubHeading>
            <RewriteTable rows={rewrites} />

            <SubHeading hint="Queries with real impression volume stuck just off page 1 (avg position ~5-20) or converting impressions poorly — the fastest ranking wins.">
                Keyword optimization gaps
            </SubHeading>
            <KeywordTable rows={gaps} />

            <SubHeading hint="High-intent queries to bid on (weak organic rank, little/no paid coverage) and terms we may be overpaying for (already ranking #1-2 organically).">
                Paid-search moves
            </SubHeading>
            {!adsOn && (
                <Alert severity="info" sx={{ mb: 1 }}>
                    Google Ads isn&apos;t connected, so ad candidates are inferred from organic signals only and
                    cannibalization can&apos;t be measured. Configure the Google Ads credentials to see actual paid coverage.
                </Alert>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                Ad candidates — worth bidding on
            </Typography>
            <Box sx={{ mb: 2 }}>
                <AdTable rows={ads.candidates} mode="candidate" />
            </Box>
            {adsOn && (
                <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                        Cannibalization — paying while ranking organically
                    </Typography>
                    <AdTable rows={ads.cannibalization} mode="cannibal" />
                </>
            )}
        </Box>
    );
};

export default SeoInsights;
